import { useState, useMemo, useCallback } from 'react';

import difference from 'lodash/difference';
import fromPairs from 'lodash/fromPairs';
import pick from 'lodash/pick';

import { AUTO_NUMBERING_MODE } from '@adsk/nirvana-auto-numbering';

import { getSequenceNumber, POSITIVE_NUMBER_REGEXP, NUMBER_REGEXP } from '../sequenceNumber';
import { useTranslate } from './useTranslate';
import { MAX_CHARACTER_DEFAULT_LENGTH } from '../consts';

const validateStart = ({
  value: { start },
  attribute: { length, minLength, maxLength },
  ignoreEmptyValue,
  T
}: {
  value: { start?: string };
  attribute: { length?: number; minLength?: number; maxLength?: number };
  ignoreEmptyValue?: boolean;
  T: any;
}) => {
  let error = '';
  // If ignoreEmptyValue is true, regard empty value as valid
  // If ignoreEmptyValue is false, regard empty value as invalid but there is no need to show error message as requirement
  if (!start) return { valid: ignoreEmptyValue, error };
  if (start.length > MAX_CHARACTER_DEFAULT_LENGTH) {
    error = T.translate('sequenceNumber.error.start.max', {
      context: MAX_CHARACTER_DEFAULT_LENGTH
    });
  } else if (length) {
    if (start.length !== length || !POSITIVE_NUMBER_REGEXP.test(start))
      error = T.translate('sequenceNumber.error.start.fix', {
        context: length
      });
  } else if (maxLength && minLength) {
    if (start.length < minLength || start.length > maxLength || !POSITIVE_NUMBER_REGEXP.test(start))
      error = T.translate('sequenceNumber.error.start.range', {
        minLength,
        maxLength
      });
  } else {
    if (!POSITIVE_NUMBER_REGEXP.test(start)) error = T.translate('sequenceNumber.error.start.any');
  }
  return { valid: !error, error };
};

const validateIncrement = ({
  value: { start = '', increment = '' },
  attribute,
  items,
  ignoreEmptyValue,
  itemsFilter,
  T
}: {
  value: { start?: string; increment?: string };
  attribute: { length?: number; minLength?: number; maxLength?: number };
  items: any[];
  ignoreEmptyValue?: boolean;
  itemsFilter?: (item: any, attribute: any) => boolean;
  T: any;
}) => {
  const { length, maxLength } = attribute;
  let error = '';
  // If ignoreEmptyValue is true, regard empty value as valid
  // If ignoreEmptyValue is false, regard empty value as invalid but there is no need to show error message as requirement
  if (!increment) return { valid: ignoreEmptyValue, error: '' };

  if (increment.length > MAX_CHARACTER_DEFAULT_LENGTH) {
    error = T.translate('sequenceNumber.error.increment.max', {
      context: MAX_CHARACTER_DEFAULT_LENGTH
    });
  } else if (!/^[-\.\d]+$/.test(increment)) {
    error = T.translate('sequenceNumber.error.increment.char');
  } else if (!NUMBER_REGEXP.test(increment)) {
    error = T.translate('sequenceNumber.error.increment.format');
  } else {
    // Increment number exceeds the range if sequence number exceeds the range
    const limitation = length || maxLength || MAX_CHARACTER_DEFAULT_LENGTH;
    const itemsCount = itemsFilter ? items.filter((item) => itemsFilter(item, attribute)).length : items.length;
    const isSequenceValid =
      Math.max(
        getSequenceNumber({ start, increment }, 0).length,
        getSequenceNumber({ start, increment }, itemsCount - 1).length
      ) <= limitation;
    if (!isSequenceValid)
      error = T.translate('sequenceNumber.error.sequence', {
        context: limitation
      });
  }

  return { valid: !error, error };
};

const useSequenceNumber = ({
  attributesMap,
  items,
  ignoreEmptyValue = false,
  itemsFilter
}: {
  attributesMap: { [key: string]: any };
  items: any[];
  ignoreEmptyValue?: boolean;
  itemsFilter?: (item: any, attribute: any) => boolean;
}) => {
  const T = useTranslate();
  const [values, setValues] = useState<{
    [key: string]: any;
  }>({});
  const [sequenceIds, setSequenceIds] = useState<string[]>([]);
  const sequenceValues = useMemo(() => pick(values, sequenceIds), [values, sequenceIds]);
  const validations = useMemo(() => {
    return fromPairs(
      Object.entries(sequenceValues).map(([id, value]) => {
        const validationStart = validateStart({
          value,
          attribute: attributesMap[id],
          ignoreEmptyValue,
          T
        });
        const validationIncrement = validateIncrement({
          value,
          attribute: attributesMap[id],
          items,
          ignoreEmptyValue,
          itemsFilter,
          T
        });
        const valid = validationStart.valid && validationIncrement.valid;
        const errors = {
          start: validationStart.error,
          increment: validationIncrement.error
        };
        return [id, { valid, errors }];
      })
    );
  }, [attributesMap, sequenceValues, items, ignoreEmptyValue, itemsFilter, T]);
  const valid = useMemo(() => Object.values(validations).every(({ valid }) => valid), [validations]);

  const handleUpdate = useCallback((attributeId: number | string, payload?: any) => {
    setValues((prev) => ({ ...prev, [attributeId]: { ...prev[attributeId], ...payload } }));
  }, []);

  const handleUpdateMode = useCallback(
    (sequenceKey: string, mode: string) => {
      if (mode === AUTO_NUMBERING_MODE.SEQUENCE) {
        // For sequence number, we set default value of increment to 1
        if (!values[sequenceKey]) setValues({ ...values, [sequenceKey]: { increment: '1' } });
        setSequenceIds((prev) => [...prev, sequenceKey]);
      } else if (mode === AUTO_NUMBERING_MODE.SAME) {
        setSequenceIds((prev) => difference(prev, [sequenceKey]));
      }
    },
    [values]
  );

  return { valid, values: sequenceValues, validations, onUpdate: handleUpdate, onUpdateMode: handleUpdateMode };
};

export { useSequenceNumber };
